import moment from 'moment';
import { useMemo } from 'react';
import { Table, TableProps, Tooltip } from 'antd';
import { WarningFilled } from '@ant-design/icons';
import { ColumnsType, ColumnType } from 'antd/es/table';
import UserAvatar from 'components/new-ui/base/avatar/UserAvatar';
import FileActions from 'components/new-ui/base/file-actions/FileActions';
import FileIcon from 'components/new-ui/base/file-icon/FileIcon';
import { StyledTableHeaderCell, StyledTableWrapper } from 'components/new-ui/base/table/styles';
import Tag from 'components/new-ui/base/tag/Tag';
import { SecurityCheckStatus } from 'types/common';
import { DeclarationDocumentStatus } from 'views/declarations/declaration-tabs/documentation/types';

const EXPIRY_WARNING_DAYS = 30;

export interface FileRecord {
    id: string;
    filename: string;
    fileType: string;
    uploadedBy: string;
    createdAt: string;
    securityCheckStatus?: SecurityCheckStatus;
    status?: DeclarationDocumentStatus;
    documentType?: string;
    archived?: boolean;
    activeFrom?: string;
    activeTo?: string;
}

interface Props<T extends FileRecord> extends TableProps<T> {
    actions?: {
        preview?: (record: T) => void;
        download?: (record: T) => void;
        delete?: (record: T) => void;
        edit?: (record: T) => void;
        archive?: (record: T) => void;
        unarchive?: (record: T) => void;
    };
    showStatusColumn?: boolean;
    showUploadedByColumn?: boolean; // FIXME: This is a temporary solution so we can hide the uploadedByColumn for declaration documents until the CMS service refactor
}

const FilesTable = <T extends FileRecord>({
    dataSource,
    actions,
    showStatusColumn,
    showUploadedByColumn = true,
    ...rest
}: Props<T>) => {
    const defaultColumns: ColumnsType<T> = useMemo(
        () => [
            {
                key: 'FILE_NAME',
                title: 'Name',
                dataIndex: 'filename',
                render: (value: string, record: T) => (
                    <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                        <FileIcon type={record.fileType} />
                        {value}
                    </div>
                ),
            },
            {
                key: 'FILE_TYPE',
                title: 'Type',
                dataIndex: 'fileType',
            },
            {
                key: 'CREATED_AT',
                title: 'Date of Creation',
                dataIndex: 'createdAt',
                render: (timestamp: string) => {
                    const formattedDate = moment(timestamp).format('DD/MM/YYYY, h:mm:ss A');
                    return <span>{formattedDate}</span>;
                },
            },
            {
                width: 1,
                align: 'right',
                key: 'ACTIONS',
                title: 'Actions',
                render: (_: any, record: T) => {
                    const isSecurityPending = record.securityCheckStatus && record.securityCheckStatus !== 'VERIFIED';
                    const disabledAllActions = !!isSecurityPending;
                    const disabledModifyActions = disabledAllActions || (record.status && record.status !== 'UPLOADED');

                    const securityPendingMsg =
                        'This file is still being checked for security. Please try again shortly.';
                    const submittedDeleteMsg =
                        'You cannot delete a document that has already been submitted to customs.';
                    const generatedDeleteMsg = 'This document was generated automatically and cannot be deleted.';
                    const submittedEditMsg = 'You cannot edit a document that has already been submitted to customs.';
                    const generatedEditMsg = 'This document was generated automatically and cannot be edited.';

                    const previewTooltip = isSecurityPending ? securityPendingMsg : 'Preview document';
                    const downloadTooltip = isSecurityPending ? securityPendingMsg : 'Download document';

                    const editTooltip =
                        record.status === 'SUBMITTED'
                            ? submittedEditMsg
                            : record.status === 'GENERATED'
                            ? generatedEditMsg
                            : isSecurityPending
                            ? securityPendingMsg
                            : 'Edit document';

                    const deleteTooltip =
                        record.status === 'GENERATED'
                            ? generatedDeleteMsg
                            : record.status === 'SUBMITTED'
                            ? submittedDeleteMsg
                            : isSecurityPending
                            ? securityPendingMsg
                            : 'Delete document';

                    const archiveTooltip = isSecurityPending ? securityPendingMsg : 'Archive document';
                    const unarchiveTooltip = isSecurityPending ? securityPendingMsg : 'Unarchive document';

                    return (
                        <FileActions
                            preview={{
                                onClick: actions?.preview ? () => actions?.preview?.(record) : undefined,
                                disabled: disabledAllActions,
                                tooltip: previewTooltip,
                            }}
                            download={{
                                onClick: actions?.download ? () => actions?.download?.(record) : undefined,
                                disabled: disabledAllActions,
                                tooltip: downloadTooltip,
                            }}
                            edit={{
                                onClick: actions?.edit ? () => actions?.edit?.(record) : undefined,
                                disabled: disabledModifyActions,
                                tooltip: editTooltip,
                            }}
                            delete={{
                                onClick: actions?.delete ? () => actions?.delete?.(record) : undefined,
                                disabled: disabledModifyActions,
                                tooltip: deleteTooltip,
                            }}
                            archive={{
                                onClick:
                                    actions?.archive && !record.archived ? () => actions?.archive?.(record) : undefined,
                                disabled: disabledAllActions,
                                tooltip: archiveTooltip,
                            }}
                            unarchive={{
                                onClick:
                                    actions?.unarchive && record.archived
                                        ? () => actions?.unarchive?.(record)
                                        : undefined,
                                disabled: disabledAllActions,
                                tooltip: unarchiveTooltip,
                            }}
                        />
                    );
                },
            },
        ],
        [actions]
    );

    const statusColumn: ColumnType<T> = useMemo(
        () => ({
            key: 'STATUS',
            title: 'Status',
            dataIndex: 'status',
            render: (status: DeclarationDocumentStatus) => {
                if (status === 'SUBMITTED') {
                    return <Tag label="Submitted" status="success" />;
                }

                if (status === 'UPLOADED') {
                    return <Tag label="Uploaded" status="default" />;
                }

                if (status === 'REJECTED') {
                    return <Tag label="Rejected" status="error" />;
                }

                if (status === 'GENERATED') {
                    return <Tag label="Generated" status="processing" />;
                }

                return <Tag label="Unknown Status" status="warning" />;
            },
        }),
        []
    );

    const uploadedByColumn: ColumnType<T> = useMemo(
        () => ({
            key: 'UPLOADED_BY',
            title: 'Uploaded By',
            dataIndex: 'uploadedBy',
            render: (value: string) => (
                <span style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                    <UserAvatar userName={value} />
                    {value}
                </span>
            ),
        }),
        []
    );

    // Only worth the space when at least one of the listed documents actually has a validity period
    const showValidityColumns = useMemo(
        () => !!dataSource?.some((record) => record.activeFrom || record.activeTo),
        [dataSource]
    );

    const validityColumns: ColumnsType<T> = useMemo(() => {
        const renderDate = (value?: string) => <span>{value ? moment(value).format('DD/MM/YYYY') : '-'}</span>;

        const renderValidTo = (value?: string) => {
            if (!value) return <span>-</span>;

            const formattedDate = moment(value).format('DD/MM/YYYY');
            const daysLeft = moment(value).endOf('day').diff(moment().startOf('day'), 'days');

            if (daysLeft > EXPIRY_WARNING_DAYS) return <span>{formattedDate}</span>;

            const hasExpired = daysLeft < 0;
            const tooltip = hasExpired
                ? `This document expired on ${formattedDate}.`
                : daysLeft === 0
                ? 'This document expires today.'
                : `This document expires in ${daysLeft} ${daysLeft === 1 ? 'day' : 'days'}.`;

            return (
                <Tooltip title={tooltip}>
                    <span
                        style={{
                            display: 'inline-flex',
                            alignItems: 'center',
                            gap: 6,
                            color: hasExpired ? 'var(--error-normal)' : 'var(--warning-normal)',
                        }}
                    >
                        <WarningFilled style={{ color: 'inherit' }} />
                        {formattedDate}
                    </span>
                </Tooltip>
            );
        };

        return [
            { key: 'ACTIVE_FROM', title: 'Valid From', dataIndex: 'activeFrom', render: renderDate },
            { key: 'ACTIVE_TO', title: 'Valid To', dataIndex: 'activeTo', render: renderValidTo },
        ];
    }, []);

    const columns: ColumnsType<T> = useMemo(() => {
        const insertBefore = (cols: ColumnsType<T>, key: string, column: ColumnType<T>) => {
            const index = cols.findIndex((col) => col.key === key);
            return index === -1 ? [...cols, column] : [...cols.slice(0, index), column, ...cols.slice(index)];
        };

        let cols = defaultColumns;
        if (showStatusColumn) cols = insertBefore(cols, 'ACTIONS', statusColumn);
        if (showUploadedByColumn) cols = insertBefore(cols, 'CREATED_AT', uploadedByColumn);
        if (showValidityColumns) {
            cols = validityColumns.reduce((acc, column) => insertBefore(acc, 'ACTIONS', column), cols);
        }
        return cols;
    }, [
        defaultColumns,
        showUploadedByColumn,
        showStatusColumn,
        statusColumn,
        uploadedByColumn,
        showValidityColumns,
        validityColumns,
    ]);

    return (
        <StyledTableWrapper>
            <Table<T>
                rowKey="id"
                dataSource={dataSource}
                columns={columns}
                components={{
                    header: {
                        cell: (props: any) => <StyledTableHeaderCell {...props} />,
                    },
                }}
                {...rest}
            />
        </StyledTableWrapper>
    );
};

export default FilesTable;
